import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'react-toastify';
import { addCategoryOption, updateCategoryOption, deleteCategoryOption } from '../api/categories';

const CategoryOptionsModal = ({ isOpen, onClose, category, onUpdate }) => {
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category?.options) {
      setOptions(category.options);
    }
  }, [category]);

  const handleAddOption = async (e) => {
    e.preventDefault();
    if (!newOption.trim()) return;

    setLoading(true);
    try {
      const option = await addCategoryOption(category.id, {
        option_value: newOption.trim(),
        display_order: options.length
      });
      setOptions([...options, option]);
      setNewOption('');
      toast.success('Option added successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to add option');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOption = (option) => {
    setEditingId(option.id);
    setEditingValue(option.option_value);
  };

  const handleUpdateOption = async (optionId) => {
    if (!editingValue.trim()) return;

    setLoading(true);
    try {
      const updated = await updateCategoryOption(optionId, {
        option_value: editingValue.trim()
      });
      setOptions(options.map((opt) => (opt.id === optionId ? updated : opt)));
      setEditingId(null);
      setEditingValue('');
      toast.success('Option updated successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update option');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this option?')) return;

    setLoading(true);
    try {
      await deleteCategoryOption(optionId);
      setOptions(options.filter((opt) => opt.id !== optionId));
      toast.success('Option deleted successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete option');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-primary-black">Manage Options</h2>
            <p className="text-sm text-gray-600 mt-1">
              Category: <span className="font-semibold">{category?.name}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Option */}
          <form onSubmit={handleAddOption} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Add New Option</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Enter option value..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                disabled={loading}
              />
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50"
                disabled={loading || !newOption.trim()}
              >
                <Plus className="w-5 h-5" />
                Add
              </button>
            </div>
          </form>

          {/* Options List */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Existing Options ({options.length})
            </h3>

            {options.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No options yet. Add your first option above.
              </div>
            ) : (
              <div className="space-y-2">
                {options.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />

                    {editingId === option.id ? (
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="flex-1 px-3 py-1 border border-accent-gold rounded focus:ring-2 focus:ring-accent-gold"
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 text-gray-800">{option.option_value}</span>
                    )}

                    <div className="flex gap-1">
                      {editingId === option.id ? (
                        <>
                          <button
                            onClick={() => handleUpdateOption(option.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                            disabled={loading}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(null);
                              setEditingValue('');
                            }}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditOption(option)}
                            className="p-2 text-gray-600 hover:text-accent-gold hover:bg-gray-200 rounded transition-colors"
                            disabled={loading}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteOption(option.id)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            disabled={loading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryOptionsModal;
